import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import PaginatedTable from "../../components/Organismos/Table/PaginatedTable";
import { ISorting, IPagination2 } from "../../interfaces/Components/ITable";
import { TrashCan } from "../../components/Atomos/Icons/TrashCan";
import Mail from "../../components/Atomos/Icons/Mail";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import { EmailTemplate } from "./types";
import { sanitizeEmailHtml } from "../../utils/emailTemplateHtml";
import { isMemberFirmRecipient } from "../../utils/wsgStaffFirms";
import ModalConfirmation from "../ManageFirm/GeneralTab/MainBox/ModalConfirmation";

const TrashCanIcon = () => (
  <div className="w-4 h-4 fill-current">
    <TrashCan />
  </div>
);

const MailIcon = () => (
  <div className="w-4 h-4 fill-current">
    <Mail />
  </div>
);

const EMAIL_PREVIEW_PLACEHOLDER_STYLE =
  "padding: 20px; font-family: Arial, sans-serif;";

const EMAIL_PREVIEW_BASE_STYLE = `
  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
  }

  body {
    box-sizing: border-box;
    width: 100%;
    padding: 16px;
    color: #111827;
    overflow-x: auto;
  }

  body * {
    box-sizing: border-box;
  }

  img {
    max-width: 100% !important;
    height: auto !important;
  }

  pre, code {
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const normalizeEmailValue = (email: string): string => email.trim().toLowerCase();

function buildPreviewDocument (content: string): string {
  const safeContent = sanitizeEmailHtml(content.trim());
  const previewContent =
    safeContent ||
    `<div style="${EMAIL_PREVIEW_PLACEHOLDER_STYLE}"><p style="color: #9ca3af;">No preview available.</p></div>`;
  const previewHead = `<meta charset="utf-8" /><style>${EMAIL_PREVIEW_BASE_STYLE}</style>`;

  if (/<html[\s>]/i.test(previewContent)) {
    if (/<head[\s>]/i.test(previewContent)) {
      return previewContent.replace(/<head([^>]*)>/i, `<head$1>${previewHead}`);
    }

    return previewContent.replace(
      /<html([^>]*)>/i,
      `<html$1><head>${previewHead}</head>`
    );
  }

  if (/<body[\s>]/i.test(previewContent)) {
    return `<!DOCTYPE html><html><head>${previewHead}</head>${previewContent}</html>`;
  }

  return `<!DOCTYPE html><html><head>${previewHead}</head><body>${previewContent}</body></html>`;
}

interface PreviewSectionProps {
  hasTemplateSelected: boolean;
  hasListSelected: boolean;
  selectedTemplate: EmailTemplate | null;
  isTemplatePreviewLoading?: boolean;
  recipientRows: {
    id: string;
    contact: string;
    email: string;
    firmId?: string;
    firmName: string;
    location: string;
    region: string;
  }[];
  isRecipientListLoading?: boolean;
  openRecipientListSignal?: number;
  excludedEmails?: string[];
  onExcludeEmail?: (email: string) => void;
  onExcludeEmails?: (emails: string[]) => void;
  serverPaginated?: boolean;
  totalRecipientCount?: number;
  onRecipientPageChange?: (
    pageIndex: number,
    pageSize: number,
    sort?: { field: string; order: "asc" | "desc" },
  ) => void;
}

export default function PreviewSection ({
  hasTemplateSelected,
  hasListSelected,
  selectedTemplate,
  isTemplatePreviewLoading = false,
  recipientRows,
  isRecipientListLoading = false,
  openRecipientListSignal = 0,
  excludedEmails = [],
  onExcludeEmail,
  onExcludeEmails,
  serverPaginated = false,
  totalRecipientCount = 0,
  onRecipientPageChange,
}: PreviewSectionProps): React.ReactElement {
  const emailPreviewFrameRef = useRef<HTMLIFrameElement | null>(null);
  const emailPreviewAnimationFrameRef = useRef<number | null>(null);
  const emailPreviewTimeoutsRef = useRef<number[]>([]);
  const [activeTab, setActiveTab] = useState<"email" | "recipientList">(
    "email"
  );

  const [recipientSort, setRecipientSort] = useState<ISorting>({
    field: "contact",
    order: "asc",
  });
  const [recipientPagination, setRecipientPagination] =
    useState<IPagination2>({ pageIndex: 0, pageSize: 50 });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showSendToSelectedModal, setShowSendToSelectedModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");

  useEffect(() => {
    if (openRecipientListSignal > 0) {
      setActiveTab("recipientList");
      setRecipientPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setActiveSearchQuery("");
      setSearchQuery("");
    }
  }, [openRecipientListSignal]);

  const clearEmailPreviewResizeTasks = useCallback(() => {
    if (emailPreviewAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(emailPreviewAnimationFrameRef.current);
      emailPreviewAnimationFrameRef.current = null;
    }

    emailPreviewTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    emailPreviewTimeoutsRef.current = [];
  }, []);

  const updateEmailPreviewHeight = useCallback(() => {
    const iframe = emailPreviewFrameRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc) {
      return;
    }

    const nextHeight = Math.max(
      doc.body?.scrollHeight ?? 0,
      doc.body?.offsetHeight ?? 0,
      doc.documentElement?.scrollHeight ?? 0,
      doc.documentElement?.offsetHeight ?? 0,
      320
    );

    const nextHeightPx = `${Math.ceil(nextHeight)}px`;
    if (iframe.style.height !== nextHeightPx) {
      iframe.style.height = nextHeightPx;
    }
  }, []);

  const scheduleEmailPreviewResize = useCallback(() => {
    clearEmailPreviewResizeTasks();

    emailPreviewAnimationFrameRef.current = window.requestAnimationFrame(() => {
      updateEmailPreviewHeight();
      emailPreviewAnimationFrameRef.current = null;
    });

    emailPreviewTimeoutsRef.current = [80, 200, 500, 1000].map((delay) =>
      window.setTimeout(updateEmailPreviewHeight, delay)
    );
  }, [clearEmailPreviewResizeTasks, updateEmailPreviewHeight]);

  const handleEmailPreviewLoad = useCallback(() => {
    const iframe = emailPreviewFrameRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc) {
      return;
    }

    scheduleEmailPreviewResize();

    Array.from(doc.images).forEach((image) => {
      if (!image.complete) {
        image.addEventListener("load", scheduleEmailPreviewResize, { once: true });
        image.addEventListener("error", scheduleEmailPreviewResize, { once: true });
      }
    });

    if (doc.fonts?.ready) {
      doc.fonts.ready.then(() => {
        scheduleEmailPreviewResize();
      }).catch(() => undefined);
    }
  }, [scheduleEmailPreviewResize]);

  useEffect(() => {
    return () => {
      clearEmailPreviewResizeTasks();
    };
  }, [clearEmailPreviewResizeTasks]);

  const selectedTemplateContent = useMemo(() => {
    if (!selectedTemplate) {
      return "";
    }
    if (selectedTemplate.htmlContent && selectedTemplate.htmlContent.trim()) {
      return selectedTemplate.htmlContent;
    }
    return `
      <div style="padding: 20px; font-family: Arial, sans-serif;">
        <h2 style="color: #0070CD;">${selectedTemplate.name}</h2>
        <p style="color: #474F56;">Year: ${selectedTemplate.year}</p>
        <p style="color: #666;">This is a preview of the email template content. In the real implementation, this would show the actual HTML template from the database.</p>
      </div>
    `;
  }, [selectedTemplate]);

  const selectedTemplatePreviewDocument = useMemo(
    () => buildPreviewDocument(selectedTemplateContent),
    [selectedTemplateContent]
  );

  useEffect(() => {
    clearEmailPreviewResizeTasks();
  }, [selectedTemplatePreviewDocument, clearEmailPreviewResizeTasks]);

  useEffect(() => {
    if (activeTab !== "email") {
      clearEmailPreviewResizeTasks();
    }
  }, [activeTab, clearEmailPreviewResizeTasks]);

  const handleToggleSelect = useCallback((id: string, firmId?: string) => {
    if (!isMemberFirmRecipient(firmId)) {
      return;
    }
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleSearch = useCallback(() => {
    setActiveSearchQuery(searchQuery.trim().toLowerCase());
    setRecipientPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchQuery]);

  const normalizedExcludedEmails = useMemo(() => {
    return new Set(
      excludedEmails.map(normalizeEmailValue).filter(Boolean)
    );
  }, [excludedEmails]);

  const availableRecipients = useMemo(() => {
    return recipientRows.filter(
      (r) => !normalizedExcludedEmails.has(normalizeEmailValue(r.email))
    );
  }, [recipientRows, normalizedExcludedEmails]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(
        availableRecipients
          .filter((r) => isMemberFirmRecipient(r.firmId))
          .map((r) => r.id),
      );
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) {
          next.add(id);
        }
      });
      return next;
    });
  }, [availableRecipients]);

  const filteredRecipients = useMemo(() => {
    let result = availableRecipients;

    if (activeSearchQuery) {
      result = result.filter(
        (r) =>
          r.contact.toLowerCase().includes(activeSearchQuery) ||
          r.email.toLowerCase().includes(activeSearchQuery) ||
          r.firmName.toLowerCase().includes(activeSearchQuery) ||
          r.location.toLowerCase().includes(activeSearchQuery) ||
          r.region.toLowerCase().includes(activeSearchQuery)
      );
    }
    return result;
  }, [availableRecipients, activeSearchQuery]);

  const selectedRecipients = useMemo(
    () => availableRecipients.filter((r) => selectedIds.has(r.id)),
    [availableRecipients, selectedIds],
  );

  const hasSelectedRecipients = selectedRecipients.length > 0;

  const excludeEmails = useCallback(
    (emails: string[]) => {
      const validEmails = emails.filter((email) => normalizeEmailValue(email));
      if (!validEmails.length) {
        return;
      }

      if (onExcludeEmails) {
        onExcludeEmails(validEmails);
        return;
      }

      if (onExcludeEmail) {
        validEmails.forEach((email) => onExcludeEmail(email));
      }
    },
    [onExcludeEmail, onExcludeEmails],
  );

  const handleRemoveRecipient = useCallback((id: string) => {
    const recipient = recipientRows.find((r) => r.id === id);
    if (recipient) {
      excludeEmails([recipient.email]);
    }
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, [recipientRows, excludeEmails]);

  const handleDeleteSelected = useCallback(() => {
    if (!selectedRecipients.length) {
      return;
    }
    excludeEmails(selectedRecipients.map((r) => r.email));
    setSelectedIds(new Set());
  }, [excludeEmails, selectedRecipients]);

  const executeSendToSelected = useCallback(() => {
    if (!selectedRecipients.length) {
      return;
    }

    const selectedRecipientIds = new Set(selectedRecipients.map((r) => r.id));
    const recipientsToExclude = availableRecipients.filter(
      (recipient) => !selectedRecipientIds.has(recipient.id),
    );

    excludeEmails(recipientsToExclude.map((r) => r.email));
    setSelectedIds(new Set());
  }, [availableRecipients, excludeEmails, selectedRecipients]);

  const handleSendToSelected = useCallback(() => {
    if (!selectedRecipients.length) {
      return;
    }
    setShowSendToSelectedModal(true);
  }, [selectedRecipients.length]);

  const handleSendToSelectedConfirmation = useCallback(
    (confirm: boolean) => {
      if (!confirm) {
        setShowSendToSelectedModal(false);
        return;
      }

      setShowSendToSelectedModal(false);
      executeSendToSelected();
    },
    [executeSendToSelected],
  );

  const sendToSelectedConfirmMessage = useMemo(() => {
    const count = selectedRecipients.length;
    const emailLabel = count === 1 ? "email" : "emails";
    return `Send email to selected recipients? ${count} ${emailLabel} will be sent.`;
  }, [selectedRecipients.length]);

  const sortedRecipients = useMemo(() => {
    if (serverPaginated) {
      return filteredRecipients;
    }

    const sorted = [...filteredRecipients];
    const { field, order } = recipientSort;
    if (!field) {
      return sorted;
    }

    sorted.sort((a, b) => {
      const aValue = (a as Record<string, unknown>)[field];
      const bValue = (b as Record<string, unknown>)[field];

      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      const compareResult = String(aValue ?? "").localeCompare(
        String(bValue ?? ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      );

      return order === "asc" ? compareResult : -compareResult;
    });

    return sorted;
  }, [filteredRecipients, recipientSort, serverPaginated]);

  useEffect(() => {
    if (serverPaginated) {
      return;
    }

    const maxPageIndex = Math.max(
      0,
      Math.ceil(sortedRecipients.length / Math.max(1, recipientPagination.pageSize)) - 1
    );
    if (recipientPagination.pageIndex > maxPageIndex) {
      setRecipientPagination((prev) => ({ ...prev, pageIndex: maxPageIndex }));
    }
  }, [sortedRecipients.length, recipientPagination.pageIndex, recipientPagination.pageSize, serverPaginated]);

  const paginatedRecipients = useMemo(() => {
    if (serverPaginated) {
      return sortedRecipients;
    }

    const start = recipientPagination.pageIndex * recipientPagination.pageSize;
    return sortedRecipients.slice(start, start + recipientPagination.pageSize);
  }, [sortedRecipients, recipientPagination.pageIndex, recipientPagination.pageSize, serverPaginated]);

  const handleRecipientPaginationChange = useCallback(
    (nextPagination: IPagination2) => {
      setRecipientPagination(nextPagination);
      if (serverPaginated && onRecipientPageChange) {
        onRecipientPageChange(
          nextPagination.pageIndex,
          nextPagination.pageSize,
          recipientSort.field
            ? {
              field: recipientSort.field,
              order: recipientSort.order === "desc" ? "desc" : "asc",
            }
            : undefined,
        );
      }
    },
    [onRecipientPageChange, recipientSort.field, recipientSort.order, serverPaginated],
  );

  const handleRecipientSortingChange = useCallback(
    (nextSort: ISorting) => {
      setRecipientSort(nextSort);
      if (serverPaginated && onRecipientPageChange) {
        const nextPagination = { ...recipientPagination, pageIndex: 0 };
        setRecipientPagination(nextPagination);
        onRecipientPageChange(
          0,
          nextPagination.pageSize,
          nextSort.field
            ? {
              field: nextSort.field,
              order: nextSort.order === "desc" ? "desc" : "asc",
            }
            : undefined,
        );
      }
    },
    [onRecipientPageChange, recipientPagination, serverPaginated],
  );

  const recipientTableTotalCount = serverPaginated
    ? totalRecipientCount
    : sortedRecipients.length;

  const recipientListTabCount = serverPaginated
    ? totalRecipientCount
    : filteredRecipients.length;

  const selectableFilteredRecipients = useMemo(
    () => filteredRecipients.filter((r) => isMemberFirmRecipient(r.firmId)),
    [filteredRecipients],
  );

  const allFilteredSelected = useMemo(() => {
    if (selectableFilteredRecipients.length === 0) {
      return false;
    }
    return selectableFilteredRecipients.every((r) => selectedIds.has(r.id));
  }, [selectableFilteredRecipients, selectedIds]);

  const handleSelectAll = useCallback(() => {
    const selectableIds = selectableFilteredRecipients.map((r) => r.id);
    const allSelected = selectableIds.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableIds));
    }
  }, [selectableFilteredRecipients, selectedIds]);

  const recipientColumns = useMemo(
    () => [
      {
        id: "select",
        header: (
          <input
            type="checkbox"
            checked={allFilteredSelected}
            onChange={handleSelectAll}
            disabled={selectableFilteredRecipients.length === 0}
            className="w-4 h-4 cursor-pointer accent-Default disabled:cursor-not-allowed"
            title="Select member firm recipients on this page"
          />
        ),
        enableSorting: false,
        width: 50,
        cell: ({ row }: { row: any }) => {
          const selectable = isMemberFirmRecipient(row.firmId);
          return (
            <div className="flex justify-center">
              <input
                type="checkbox"
                checked={selectable && selectedIds.has(row.id)}
                onChange={() => handleToggleSelect(row.id, row.firmId)}
                disabled={!selectable}
                className={`w-4 h-4 accent-Default ${selectable ? "cursor-pointer" : "cursor-not-allowed opacity-40"
                  }`}
                title={
                  selectable
                    ? undefined
                    : "Member firm recipients only"
                }
                aria-label={
                  selectable
                    ? `Select ${row.contact}`
                    : "Recipient not eligible for selection"
                }
              />
            </div>
          );
        },
      },
      {
        id: "count",
        header: "#",
        enableSorting: false,
        cell: ({
          rowIndex,
        }: {
          rowIndex: number;
          row: any;
          value: any;
        }) => (
          <p>
            {rowIndex +
              1 +
              recipientPagination.pageIndex * recipientPagination.pageSize}
          </p>
        ),
      },
      { id: "contact", header: "Contact" },
      { id: "email", header: "Email" },
      { id: "firmName", header: "Firm" },
      { id: "location", header: "Location" },
      { id: "region", header: "Region" },
      {
        id: "action",
        header: "Action",
        enableSorting: false,
        width: 80,
        cell: ({ row }: { row: any }) => (
          <button
            type="button"
            onClick={() => handleRemoveRecipient(row.id)}
            className="text-feedback-error hover:text-primary transition-colors"
            aria-label="Remove recipient"
          >
            <div className="w-5 h-5 fill-current">
              <TrashCan />
            </div>
          </button>
        ),
      },
    ],
    [
      recipientPagination.pageIndex,
      recipientPagination.pageSize,
      handleRemoveRecipient,
      selectedIds,
      allFilteredSelected,
      selectableFilteredRecipients.length,
      handleSelectAll,
      handleToggleSelect,
    ],
  );

  return (
    <div>
      <ModalConfirmation
        openModal={showSendToSelectedModal}
        setOpenModal={setShowSendToSelectedModal}
        setConfirmation={handleSendToSelectedConfirmation}
        title="Send to Selected"
        message={sendToSelectedConfirmMessage}
      />
      <h2 className="text-[2.625rem] font-decimal font-medium leading-none text-Default mb-4">
        Recipient List / Preview Email
      </h2>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 lg:px-8 pt-5 flex gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("email")}
            className={`px-8 h-[42px] text-sm font-medium tracking-wide transition-colors rounded-none ${activeTab === "email"
              ? "bg-accent-Default-dark text-white"
              : "bg-gray-100 text-gray-font hover:bg-gray-200"
              }`}
          >
            PREVIEW EMAIL
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("recipientList")}
            className={`px-8 h-[42px] text-sm font-medium tracking-wide transition-colors rounded-none ${activeTab === "recipientList"
              ? "bg-accent-Default-dark text-white"
              : "bg-gray-100 text-gray-font hover:bg-gray-200"
              }`}
          >
            RECIPIENT LIST {hasListSelected && `(${recipientListTabCount})`}
          </button>
        </div>

        <div className="px-6 lg:px-8 py-6">
          {activeTab === "email" ? (
            hasTemplateSelected ? (
              isTemplatePreviewLoading ? (
                <div className="flex items-center justify-center py-16 bg-gray-50 rounded">
                  <p className="text-gray-400 text-lg">Loading email preview...</p>
                </div>
              ) : (
                <div className="rounded border border-gray-100 bg-white overflow-hidden">
                  <iframe
                    ref={emailPreviewFrameRef}
                    title="Email preview"
                    srcDoc={selectedTemplatePreviewDocument}
                    sandbox="allow-same-origin"
                    className="block w-full border-0 bg-white min-h-[320px]"
                    onLoad={handleEmailPreviewLoad}
                  />
                </div>
              )
            ) : (
              <div className="flex items-center justify-center py-16 bg-gray-50 rounded">
                <p className="text-gray-400 text-lg">
                  Please select a template to preview
                </p>
              </div>
            )
          ) : (
            hasListSelected ? (
              isRecipientListLoading ? (
                <div className="flex items-center justify-center py-16 bg-gray-50 rounded">
                  <p className="text-gray-400 text-lg">Loading recipients...</p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      placeholder={
                        serverPaginated
                          ? "Search current page..."
                          : "Search recipients..."
                      }
                      className="w-full min-w-0 flex-1 px-4 py-2.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-Default"
                    />
                    <div className="flex flex-wrap items-center gap-3 h-auto">
                      <LegacyButton
                        text="SEARCH"
                        color="accent"
                        style="filled"
                        size="small"
                        className="!min-h-[42px] !min-w-[170px] !px-4"
                        onClick={handleSearch}
                      />
                      <LegacyButton
                        text="DELETE SELECTED"
                        color="delete"
                        style="filled"
                        size="small"
                        className="!min-h-[42px] !min-w-[210px] !px-4 !whitespace-nowrap"
                        disabled={!hasSelectedRecipients}
                        onClick={handleDeleteSelected}
                        content="textIcon"
                        icon={<TrashCanIcon />}
                        textIconLayout="cluster"
                      />
                      <LegacyButton
                        text="SEND TO SELECTED"
                        color="accent"
                        style="filled"
                        size="small"
                        className="!min-h-[42px] !min-w-[210px] !px-4 !whitespace-nowrap"
                        disabled={!hasSelectedRecipients}
                        onClick={handleSendToSelected}
                        content="textIcon"
                        icon={<MailIcon />}
                        textIconLayout="cluster"
                      />
                    </div>
                  </div>

                  <PaginatedTable
                    data={paginatedRecipients}
                    columnsDef={recipientColumns}
                    sorting={recipientSort}
                    onSortingChange={handleRecipientSortingChange}
                    totalCount={recipientTableTotalCount}
                    pagination={recipientPagination}
                    onPaginationChange={handleRecipientPaginationChange}
                    showExcelExport={false}
                    showColumnVisibility={false}
                  />
                </div>
              )
            ) : (
              <div className="flex items-center justify-center py-16 bg-gray-50 rounded">
                <p className="text-gray-400 text-lg">
                  Please select a recipient list, Test List members, or create a new
                  list
                </p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
